import { useEffect, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import cytoscape from 'cytoscape'
import { exploreConnections, discoverMore } from '../../services/api'
import { NODE_TYPES, ENTITY_SUBTYPE_STYLES } from '../../constants/nodeTypes'
import { sanitizeNodeLabel } from '../../utils/nodeLabel'
import { COSE_BASE_LAYOUT } from './cytoscapeLayoutConfig'
import type { ConnectionsNode, ConnectionsEdge } from '../../types'

type ExploreFn = (id: string, ns?: string) => Promise<{ nodes: ConnectionsNode[]; edges: ConnectionsEdge[]; center: string }>
type DiscoverFn = (nodeId: string, nodeType: string, ns?: string) => Promise<{ nodes: ConnectionsNode[]; edges: ConnectionsEdge[]; expanded_from: string }>

interface ConnectionsGraphProps {
  documentId: string | null
  layout: string
  onNodeSelect: (node: ConnectionsNode | null) => void
  resetCounter?: number
  hiddenNodeTypes?: Set<string>
  hiddenEdgeTypes?: Set<string>
  /** BUG-NEW-J: project_id for namespace isolation of connection queries */
  projectId?: string
  /** #4612/#4646: pluggable data source — defaults to the invention/document
   * connections API (exploreConnections/discoverMore) so every existing
   * caller (Project/Document Connections) is unaffected. The Patent
   * Connections panel passes patent-scoped fetchers backed by
   * /patents/{id}/connections instead. */
  fetchExplore?: ExploreFn
  fetchDiscover?: DiscoverFn
}

/** Node types that open the side panel on click (Wave 2: all 8 invention types
 * + #4612/#4646: similar_patent/family also expand in place, matching the
 * project graph's behaviour — the other 4 patent node types (inventor,
 * assignee, cpc, patent-centre) get a navigation action instead, not
 * server-side expansion, so they stay out of this set. */
const EXPANDABLE_TYPES = new Set([
  'invention', 'journal', 'project', 'collaborator', 'topic', 'discovery', 'entity', 'document',
  'similar_patent', 'family',
])

/** Build layout options with COSE-specific tuning for better spacing */
function getLayoutOptions(layout: string, animate = true) {
  const base = { name: layout, animate, fit: true }
  if (layout === 'cose') {
    return {
      ...COSE_BASE_LAYOUT,
      animate,
      animationDuration: animate ? 500 : 0,
    } as cytoscape.LayoutOptions
  }
  return { ...base, animationDuration: animate ? 500 : 0 } as cytoscape.LayoutOptions
}

/** localStorage-based position persistence */
const POSITION_KEY_PREFIX = 'graph-positions-'

export function savePositions(cy: cytoscape.Core, documentId: string) {
  const positions: Record<string, { x: number; y: number }> = {}
  cy.nodes().forEach((n) => {
    const pos = n.position()
    positions[n.id()] = { x: pos.x, y: pos.y }
  })
  try {
    localStorage.setItem(POSITION_KEY_PREFIX + documentId, JSON.stringify(positions))
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

export function loadPositions(documentId: string): Record<string, { x: number; y: number }> | null {
  try {
    const raw = localStorage.getItem(POSITION_KEY_PREFIX + documentId)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export function clearPositions(documentId: string) {
  try {
    localStorage.removeItem(POSITION_KEY_PREFIX + documentId)
  } catch {
    // silently ignore
  }
}

/**
 * Compute the minimum pairwise Euclidean distance across all node pairs in the
 * graph (graph-coordinate pixels). Returns 0 for graphs with fewer than 2 nodes
 * or when all nodes coincide.
 */
export function computeMinPairwiseDist(cy: cytoscape.Core): number {
  const positions: Array<{ x: number; y: number }> = []
  cy.nodes().forEach((n) => {
    positions.push({ x: n.position('x'), y: n.position('y') })
  })
  if (positions.length < 2) return 0
  let minDist = Infinity
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const dx = positions[i].x - positions[j].x
      const dy = positions[i].y - positions[j].y
      const d = Math.sqrt(dx * dx + dy * dy)
      if (d > 0 && d < minDist) minDist = d
    }
  }
  return minDist === Infinity ? 0 : minDist
}

/**
 * Compute the zoom level at which labels can safely appear without overlap.
 * Labels are text-max-width:80px → 40px each side of node centre + 5px safety
 * margin = 85px. Two adjacent labels clear when zoom × minPairwiseDist > 85.
 * Returns 0 for trivial graphs (always show labels).
 */
export function computeLabelRevealZoom(cy: cytoscape.Core): number {
  const minDist = computeMinPairwiseDist(cy)
  if (minDist <= 0) return 0
  return 85 / minDist
}

/**
 * Update the cytoscape STYLESHEET RULE for the `node` selector's `label`
 * property based on whether the current zoom is ≥ revealZoom. Uses
 * cy.style().selector().style().update() — modifies the stylesheet itself,
 * NOT inline node styles (which are immediately overridden by this
 * component's style-listener-triggered React re-render).
 */
export function applyLabelStylesheet(cy: cytoscape.Core, revealZoom: number): boolean {
  const show = cy.zoom() >= revealZoom
  cy.style()
    .selector('node')
    .style('label', show ? 'data(label)' : '')
    .update()
  return show
}

/** Check if a node should be hidden based on its type/subtype and the hidden set. */
function isNodeHidden(nodeData: { type?: string; subtype?: string }, hidden?: Set<string>): boolean {
  if (!hidden || hidden.size === 0) return false
  const type = nodeData.type || ''
  if (type === 'entity' && nodeData.subtype) {
    return hidden.has(`entity:${nodeData.subtype}`)
  }
  return hidden.has(type)
}

export default function ConnectionsGraph({ documentId, layout, onNodeSelect, resetCounter = 0, hiddenNodeTypes, hiddenEdgeTypes, projectId, fetchExplore = exploreConnections, fetchDiscover = discoverMore }: ConnectionsGraphProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const cyRef = useRef<cytoscape.Core | null>(null)
  const expandedRef = useRef<Set<string>>(new Set())
  // Zoom threshold above which labels can safely appear. Recomputed after
  // every layout from actual node positions; read on every zoom event.
  const revealZoomRef = useRef<number>(0)
  // BUG-140: keep the latest visibility filters in refs so the show/hide pass
  // can be re-run from element-mutating callbacks (node expand / data rebuild)
  // without adding the filter sets to those effects' dependency arrays (which
  // would rebuild the whole graph on every legend toggle).
  const hiddenNodeTypesRef = useRef<Set<string> | undefined>(hiddenNodeTypes)
  const hiddenEdgeTypesRef = useRef<Set<string> | undefined>(hiddenEdgeTypes)
  hiddenNodeTypesRef.current = hiddenNodeTypes
  hiddenEdgeTypesRef.current = hiddenEdgeTypes

  /**
   * BUG-140: apply node/edge visibility from the CURRENT filter refs. Called
   * whenever the element set changes (expand, reload) as well as when the
   * filters themselves change, so the legend and the canvas never disagree.
   */
  const applyVisibility = useCallback((cy: cytoscape.Core) => {
    const hNodes = hiddenNodeTypesRef.current
    const hEdges = hiddenEdgeTypesRef.current
    cy.batch(() => {
      cy.nodes().forEach((n) => {
        if (isNodeHidden(n.data(), hNodes)) {
          n.style('display', 'none')
        } else {
          n.removeStyle('display')
        }
      })
      cy.edges().forEach((e) => {
        const edgeType = e.data('type') || ''
        if (hEdges?.has(edgeType)) {
          e.style('display', 'none')
        } else {
          e.removeStyle('display')
        }
      })
    })
  }, [])

  const { data, isLoading } = useQuery({
    queryKey: ['connections', documentId, projectId],
    queryFn: () => fetchExplore(documentId!, projectId),
    enabled: !!documentId,
  })

  // Reset expanded tracking when the root document changes
  useEffect(() => {
    expandedRef.current = new Set()
  }, [documentId])

  /** Merge discovered nodes/edges into the live cytoscape instance */
  const expandNode = useCallback(async (nodeId: string, nodeType: string) => {
    const cy = cyRef.current
    if (!cy || expandedRef.current.has(nodeId)) return
    if (!EXPANDABLE_TYPES.has(nodeType)) return

    expandedRef.current.add(nodeId)

    try {
      const result = await fetchDiscover(nodeId, nodeType, projectId)

      // Add only nodes that don't already exist in the graph
      result.nodes.forEach((node: ConnectionsNode) => {
        if (cy.getElementById(node.id).length === 0) {
          cy.add({
            data: { id: node.id, label: sanitizeNodeLabel(node.label), type: node.type, subtype: node.subtype, score: node.score },
          })
        }
      })

      // Add only edges whose ID doesn't already exist (handle multiple shared_entity edges)
      result.edges.forEach((edge: ConnectionsEdge) => {
        let edgeId = `${edge.source}-${edge.type}-${edge.target}`
        let idx = 0
        while (cy.getElementById(edgeId).length > 0) {
          idx++
          edgeId = `${edge.source}-${edge.type}-${edge.target}-${idx}`
        }
        // Skip if either node doesn't exist in graph
        if (cy.getElementById(edge.source).length === 0 || cy.getElementById(edge.target).length === 0) return
        cy.add({
          data: { id: edgeId, source: edge.source, target: edge.target, type: edge.type, weight: edge.weight },
        })
      })

      // Lock existing nodes with saved positions so only new nodes get positioned
      const saved = documentId ? loadPositions(documentId) : null
      if (saved) {
        cy.nodes().forEach((n) => {
          const pos = saved[n.id()]
          if (pos) {
            n.position(pos)
            n.lock()
          }
        })
      }

      // Re-layout — only unlocked (new) nodes will be repositioned. The
      // layoutstop listener recomputes min-dists, re-applies label visibility,
      // and (BUG-139) persists the SETTLED positions once the animation ends.
      cy.layout(getLayoutOptions(layout)).run()

      // Unlock all nodes after layout so they remain draggable
      cy.nodes().unlock()

      // BUG-140: newly-added nodes/edges must respect the active visibility
      // filters — re-run the show/hide pass so the legend and canvas agree.
      applyVisibility(cy)

      // Highlight the expanded node briefly
      const expandedEle = cy.getElementById(nodeId)
      if (expandedEle.length) {
        expandedEle.style('border-width', 3)
        expandedEle.style('border-color', '#0EA5E9')
      }
    } catch (e) {
      // If discover fails (e.g. topic node with no embedding), just ignore
      expandedRef.current.delete(nodeId)
    }
  }, [layout, documentId, applyVisibility, fetchDiscover])

  useEffect(() => {
    if (!containerRef.current) return

    const cy = cytoscape({
      container: containerRef.current,
      style: [
        {
          selector: 'node',
          style: {
            // Stream C1 (#2785): bumped font-size 10→12, weight 600, opacity
            // 0.7→0.95, max-width 80→120, wrap mode ellipsis→wrap so longer
            // labels stay legible without aggressive truncation.
            label: 'data(label)',
            'text-valign': 'bottom',
            'text-halign': 'center',
            'font-size': '12px',
            'font-weight': 600,
            color: '#1F2937',
            'text-margin-y': 4,
            // 'ellipsis' + 80px: truncate long titles so label footprint stays
            // narrow enough that zoom-threshold reveal works correctly.
            // Full label always available on hover (tooltip).
            'text-wrap': 'ellipsis',
            'text-max-width': '80px',
            'text-background-color': '#ffffff',
            'text-background-opacity': 0.95,
            'text-background-padding': '3px',
            'text-background-shape': 'roundrectangle',
            'text-border-color': '#E5E7EB',
            'text-border-opacity': 1,
            'text-border-width': 0.5,
            width: 30,
            height: 30,
          },
        },
        ...Object.entries(NODE_TYPES).map(([type, config]) => ({
          selector: `node[type="${type}"]`,
          style: {
            shape: config.shape as cytoscape.Css.NodeShape,
            'background-color': config.color,
          },
        })),
        {
          selector: 'edge',
          style: {
            width: 1.5,
            'line-color': '#CBD5E1',
            'target-arrow-color': '#CBD5E1',
            'target-arrow-shape': 'triangle' as const,
            'curve-style': 'bezier' as const,
            opacity: 0.7,
          },
        },
        // Entity subtype-specific colours
        ...Object.entries(ENTITY_SUBTYPE_STYLES).map(([subtype, config]) => ({
          selector: `node[type="entity"][subtype="${subtype}"]`,
          style: {
            'background-color': config.color,
          },
        })),
        {
          selector: 'node[type="topic"]',
          style: {
            width: 20,
            height: 20,
            'font-size': '8px',
          },
        },
        {
          selector: 'edge[type="shared_entity"]',
          style: {
            'line-style': 'dashed' as const,
            'line-color': '#8B5CF6',
            'target-arrow-color': '#8B5CF6',
            width: 1.5,
            opacity: 0.6,
          },
        },
        {
          selector: 'edge[type="uses_material"]',
          style: {
            'line-color': '#14B8A6',
            'target-arrow-color': '#14B8A6',
          },
        },
        {
          selector: 'edge[type="uses_method"]',
          style: {
            'line-color': '#F59E0B',
            'target-arrow-color': '#F59E0B',
          },
        },
        {
          selector: 'edge[type="applies_to"]',
          style: {
            'line-color': '#F43F5E',
            'target-arrow-color': '#F43F5E',
          },
        },
        {
          selector: 'edge[type="predicted_connection"]',
          style: {
            'line-style': 'dashed' as const,
            'line-color': '#60A5FA',
            'target-arrow-color': '#60A5FA',
            width: 1.5,
            opacity: 0.8,
            label: 'data(confidence_label)',
            'font-size': '8px',
            color: '#60A5FA',
            'text-rotation': 'autorotate' as const,
          },
        },
        {
          selector: 'edge[type="same_project"]',
          style: {
            'line-color': '#D1D5DB',
            'target-arrow-color': '#D1D5DB',
            'line-style': 'dashed' as const,
            width: 1,
            opacity: 0.5,
          },
        },
        {
          selector: 'node:selected',
          style: {
            'border-width': 3,
            'border-color': '#0EA5E9',
          },
        },
        {
          selector: 'node[?expanded]',
          style: {
            'border-width': 2,
            'border-color': '#94A3B8',
            'border-style': 'dashed' as const,
          },
        },
      ],
      layout: getLayoutOptions(layout, false),
      minZoom: 0.3,
      maxZoom: 3,
    })

    cy.on('tap', 'node', (e) => {
      const nodeData = e.target.data()
      onNodeSelect({
        id: nodeData.id,
        label: nodeData.label,
        type: nodeData.type,
        score: nodeData.score,
      })
      // Expand connections from this node
      expandNode(nodeData.id, nodeData.type)
    })

    cy.on('tap', (e) => {
      if (e.target === cy) onNodeSelect(null)
    })

    // Hover tooltip for truncated labels
    const tooltipDiv = document.createElement('div')
    tooltipDiv.style.cssText = 'display:none;position:absolute;z-index:10;background:#fff;border:1px solid #d1d5db;border-radius:6px;padding:6px 10px;font-size:12px;color:#374151;max-width:300px;word-wrap:break-word;box-shadow:0 2px 8px rgba(0,0,0,0.1);pointer-events:none;'
    containerRef.current?.appendChild(tooltipDiv)

    cy.on('mouseover', 'node', (evt) => {
      const node = evt.target
      tooltipDiv.textContent = node.data('label') || ''
      tooltipDiv.style.display = 'block'
      const pos = node.renderedPosition()
      tooltipDiv.style.left = `${pos.x + 20}px`
      tooltipDiv.style.top = `${pos.y - 10}px`
    })
    cy.on('mouseout', 'node', () => {
      tooltipDiv.style.display = 'none'
    })

    // Save positions after any node is dragged
    cy.on('free', 'node', () => {
      if (documentId) savePositions(cy, documentId)
    })

    cyRef.current = cy

    // After every layout completes: recompute the global label-reveal zoom
    // from actual node positions, then IMMEDIATELY apply via stylesheet rule.
    // Uses cy.style() (not cy.nodes().style()) because inline node styles are
    // overridden by this component's style-listener-triggered React re-render.
    cy.on('layoutstop', () => {
      const revealZoom = computeLabelRevealZoom(cy)
      revealZoomRef.current = revealZoom
      applyLabelStylesheet(cy, revealZoom)
      // BUG-139: persist node positions only AFTER the (animated) layout has
      // SETTLED. The previous code saved immediately after calling layout.run(),
      // capturing the pre-animation clump at the origin — so the next visit
      // restored a collapsed graph. Guard on node count so the initial
      // empty-graph layout doesn't overwrite good saved positions with {}.
      if (documentId && cy.nodes().length > 0) savePositions(cy, documentId)
    })

    // On user zoom: re-apply stylesheet rule using the stored reveal threshold.
    cy.on('zoom', () => {
      applyLabelStylesheet(cy, revealZoomRef.current)
    })

    return () => {
      tooltipDiv.remove()
      cy.destroy()
    }
  }, [layout, onNodeSelect, expandNode])

  useEffect(() => {
    if (!cyRef.current || !data) return
    const cy = cyRef.current

    cy.elements().remove()

    const seenNodeIds = new Set<string>()
    data.nodes.forEach((node) => {
      if (seenNodeIds.has(node.id)) return
      seenNodeIds.add(node.id)
      cy.add({
        data: { id: node.id, label: sanitizeNodeLabel(node.label), type: node.type, subtype: node.subtype, score: node.score },
      })
    })

    const seenEdgeIds = new Set<string>()
    data.edges.forEach((edge) => {
      let edgeId = `${edge.source}-${edge.type}-${edge.target}`
      // Handle multiple edges of the same type between the same pair (e.g. shared_entity)
      let idx = 0
      while (seenEdgeIds.has(edgeId)) {
        idx++
        edgeId = `${edge.source}-${edge.type}-${edge.target}-${idx}`
      }
      seenEdgeIds.add(edgeId)
      // Skip edges referencing nodes that don't exist
      if (!seenNodeIds.has(edge.source) || !seenNodeIds.has(edge.target)) return
      const edgeData: Record<string, unknown> = { id: edgeId, source: edge.source, target: edge.target, type: edge.type, weight: edge.weight }
      if (edge.type === 'predicted_connection' && edge.confidence != null) {
        edgeData.confidence_label = `${Math.round(edge.confidence * 100)}%`
      }
      cy.add({ data: edgeData })
    })

    // Mark the center node as already expanded (it was the explore source)
    if (data.center) {
      expandedRef.current.add(data.center)
    }

    // BUG-140: apply the active visibility filters to the freshly-rebuilt
    // element set so hidden node/edge types stay hidden after a reload.
    applyVisibility(cy)

    // Restore saved positions if available, otherwise run layout
    const saved = documentId ? loadPositions(documentId) : null
    const nodeIds = cy.nodes().map((n) => n.id())
    const allHaveSaved = saved && nodeIds.length > 0 && nodeIds.every((id) => id in saved)
    if (allHaveSaved && saved) {
      cy.nodes().forEach((n) => {
        const pos = saved[n.id()]
        if (pos) n.position(pos)
      })
      cy.fit()
      // No layoutstop fires when restoring saved positions — compute + apply directly.
      const revealZoom = computeLabelRevealZoom(cy)
      revealZoomRef.current = revealZoom
      applyLabelStylesheet(cy, revealZoom)
    } else {
      // Preemptively hide labels via stylesheet so there's no flash of overlapping
      // labels during layout computation. layoutstop will toggle them back on
      // if the resulting graph is sparse enough.
      cy.style().selector('node').style('label', '').update()
      cy.layout(getLayoutOptions(layout)).run()
    }
  }, [data, layout, applyVisibility])

  // Apply visibility when the hidden type sets change. The heavy lifting lives
  // in applyVisibility (shared with the expand / reload paths for BUG-140) so
  // there is a single source of truth for node/edge visibility.
  useEffect(() => {
    const cy = cyRef.current
    if (!cy) return
    applyVisibility(cy)
  }, [hiddenNodeTypes, hiddenEdgeTypes, applyVisibility])

  // Reset layout when resetCounter changes (user clicked Reset Layout button)
  useEffect(() => {
    if (resetCounter === 0) return // skip initial render
    const cy = cyRef.current
    if (!cy || !documentId) return
    clearPositions(documentId)
    // layoutstop listener recomputes distances and applies label visibility.
    cy.layout(getLayoutOptions(layout)).run()
  }, [resetCounter]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!documentId) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500">
        <p className="text-sm">Select a project to explore connections</p>
      </div>
    )
  }

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 size={16} className="animate-spin text-iflow-500" />
            Loading connections…
          </div>
        </div>
      )}
    </div>
  )
}
